/**
 * `onboard` — 3 qadam, BITTA ekran (SPEC §onboard, faza 4).
 *
 * Nega u oxirgi fazada yozildi: onboarding mahsulotni tushuntiradi,
 * ya'ni mahsulot avval mavjud bo'lishi kerak. Boshida yozilsa u
 * hali qaror qilinmagan narsalarni va'da qilardi.
 *
 * **Prototipdan bitta CHETLANISH** (SPEC §onboard da yozilgan):
 * prototipda jins tanlanishi bilan darhol 2-qadamga sakrardi va yosh
 * o'tkazib yuborilardi. Yosh esa 16+ tekshiruvi uchun MAJBURIY
 * (D-018), shuning uchun qadam tugma bilan o'tiladi.
 *
 * **`O'tkazish` jins va yoshni ham o'tkazib yuboradi** — bu ataylab.
 * Majburiy onboarding foydalanuvchini yo'qotadi; ma'lumotsiz odam
 * 1v1 ga borganda `PROFILE_INCOMPLETE` oladi va `/profile/edit` ga
 * yo'naltiriladi (D-126). Ya'ni yo'l yopilmaydi, faqat kechiktiriladi.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { uz } from '../i18n/uz';
import { useMe, useUpdateMe } from '../lib/api/useMe';
import { request } from '../lib/api/client';
import { Chip, CTAButton, Toast } from '../ui';
import { useToast } from '../ui/useToast';
import './screens.css';

/** D-018 — 16+. Server ham tekshiradi (`UNDERAGE`). */
const MIN_AGE = 16;

/** Yosh oralig'i — `match` ekranidagi chiplar bilan BIR XIL bo'lishi shart. */
const AGE_RANGES = ['16-22', '23-27', '28-35', '35+'] as const;
type AgeRange = (typeof AGE_RANGES)[number];

/** Oraliqning O'RTASI — tug'ilgan yilni undan hisoblaymiz.
 *  Aniq yosh onboardingda so'ralmaydi: u yana bitta maydon va yana
 *  bitta tashlab ketish sababi bo'lardi. Aniq yilni keyin
 *  `/profile/edit` da ko'rsatish mumkin. */
const RANGE_MID: Record<AgeRange, number> = {
  '16-22': 19,
  '23-27': 25,
  '28-35': 31,
  '35+': 40,
};

export function Onboard() {
  const navigate = useNavigate();
  const toast = useToast();
  const me = useMe();
  const update = useUpdateMe();

  const [step, setStep] = useState(0);
  const [gender, setGender] = useState<'male' | 'female' | null>(null);
  const [range, setRange] = useState<AgeRange | null>(null);
  const [busy, setBusy] = useState(false);

  const finish = () => navigate('/', { replace: true });

  async function saveProfile(): Promise<boolean> {
    if (!gender) {
      toast.show(uz.onboard.pickGender);
      return false;
    }
    if (!range) {
      toast.show(uz.onboard.pickAge);
      return false;
    }

    const birthYear = new Date().getFullYear() - RANGE_MID[range];
    try {
      await update.mutateAsync({ gender, birth_year: birthYear });
      return true;
    } catch (err) {
      const code = (err as { code?: string }).code;
      toast.show(
        code === 'UNDERAGE'
          ? uz.onboard.underage(String(MIN_AGE))
          : err instanceof Error
            ? err.message
            : uz.common.error,
      );
      return false;
    }
  }

  async function acceptAndEnter(): Promise<void> {
    setBusy(true);
    try {
      // Offerta qabuli — D-018, MAJBURIY. Idempotent, shuning uchun
      // qayta bosilsa ham xato bermaydi.
      const pending = me.data?.pending_consents ?? ['offer', 'privacy'];
      for (const doc of pending) {
        await request('/me/consents', { method: 'POST', body: { doc_type: doc } });
      }

      // Mikrofon ruxsati — brauzer so'raydi. RAD ETILSA ham kiramiz:
      // room'ni tinglash uchun mikrofon kerak emas va odamni kirish
      // eshigida to'xtatish eng yomon yechim bo'lardi (D-083).
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((track) => track.stop());
      } catch {
        toast.show(uz.onboard.micDenied);
      }

      finish();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : uz.common.error);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="screen onboard">
      {toast.message && <Toast message={toast.message} />}

      <div className="onboard__top">
        <div className="onboard__badge">
          <span className="onboard__dot" />
          {uz.onboard.badge}
        </div>
        <button type="button" className="onboard__skip" onClick={finish}>
          {uz.onboard.skip}
        </button>
      </div>

      <div className="onboard__body">
        {step === 0 && <Hook />}
        {step === 1 && (
          <Who gender={gender} range={range} onGender={setGender} onRange={setRange} />
        )}
        {step === 2 && <Mic />}
      </div>

      <div className="onboard__tail">
        <div className="onboard__steps" aria-hidden="true">
          {[0, 1, 2].map((index) => (
            <span
              key={index}
              className={index === step ? 'onboard__step onboard__step--on' : 'onboard__step'}
            />
          ))}
        </div>

        {step === 0 && <CTAButton onClick={() => setStep(1)}>{uz.onboard.hookCta}</CTAButton>}

        {step === 1 && (
          <CTAButton
            onClick={() => void saveProfile().then((ok) => ok && setStep(2))}
            disabled={update.isPending}
          >
            {uz.onboard.whoCta}
          </CTAButton>
        )}

        {step === 2 && (
          <>
            <CTAButton onClick={() => void acceptAndEnter()} disabled={busy}>
              {uz.onboard.micCta}
            </CTAButton>
            {/* Rozilik matni CTA OSTIDA va ko'rinadi — D-018 buni
                talab qiladi va yashirilgan rozilik rozilik emas. */}
            <p className="onboard__consent">{uz.onboard.consent}</p>
          </>
        )}
      </div>
    </div>
  );
}

/* ── 0-qadam ── */

function Hook() {
  return (
    <>
      <h1 className="onboard__hook">
        {uz.onboard.hookLine1}
        <br />
        {/* Ikkinchi qator gradient matn (SPEC §onboard). */}
        <span className="gradient-text">{uz.onboard.hookLine2}</span>
      </h1>
      <p className="onboard__hint">{uz.onboard.hookHint}</p>
    </>
  );
}

/* ── 1-qadam ── */

function Who({
  gender,
  range,
  onGender,
  onRange,
}: {
  gender: 'male' | 'female' | null;
  range: AgeRange | null;
  onGender: (value: 'male' | 'female') => void;
  onRange: (value: AgeRange) => void;
}) {
  return (
    <>
      <h1 className="onboard__title">{uz.onboard.whoTitle}</h1>
      <p className="onboard__hint">{uz.onboard.whoHint}</p>

      <div className="field-label">{uz.onboard.gender}</div>
      <div className="gender-grid onboard__gender">
        <button
          type="button"
          className="gender-card"
          aria-pressed={gender === 'male'}
          onClick={() => onGender('male')}
        >
          <div className="gender-card__symbol gender-card__symbol--male">♂</div>
          <div className="gender-card__name">{uz.onboard.male}</div>
        </button>
        <button
          type="button"
          className="gender-card"
          aria-pressed={gender === 'female'}
          onClick={() => onGender('female')}
        >
          <div className="gender-card__symbol gender-card__symbol--female">♀</div>
          <div className="gender-card__name">{uz.onboard.female}</div>
        </button>
      </div>

      <div className="field-label">{uz.onboard.age}</div>
      <div className="chip-row">
        {AGE_RANGES.map((value) => (
          <Chip
            key={value}
            className="chip--age tnum"
            active={range === value}
            onClick={() => onRange(value)}
          >
            {value}
          </Chip>
        ))}
      </div>
    </>
  );
}

/* ── 2-qadam ── */

function Mic() {
  return (
    <>
      <h1 className="onboard__title">{uz.onboard.micTitle}</h1>
      <div className="onboard__lines">
        <p className="onboard__hint">{uz.onboard.micLine1}</p>
        {/* D-033: ovoz yozib olinmaydi. Buni ochiq aytish kerak —
            ovozli ilovada birinchi savol aynan shu. */}
        <p className="onboard__hint">{uz.onboard.micLine2}</p>
      </div>
    </>
  );
}
